import React, { useState, useEffect } from "react";
import ClientBackground from "./ClientBackground";
import { ChevronLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import LoadingSpinner from "./LoadingSpinner";

export default function Alerts() {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true);

        const questionsResponse = await api.get(
          `/children/weekly-checklist-questions/by-child/?child_id=${childId}`
        );
        const responsesResponse = await api.get(
          `/children/weekly-checklist-responses/current-week/?child_id=${childId}`
        );

        const allQuestions =
          questionsResponse.data.question_sets?.flatMap(
            (qs) => qs.questions
          ) || [];
        const answeredQuestionIds = new Set(
          responsesResponse.data?.map((resp) => resp.question) || []
        );

        const processedAlerts = allQuestions.map((question) => ({
          id: question.id,
          title: question.text,
          description: answeredQuestionIds.has(question.id)
            ? "Your little one just mastered this milestone. Celebrate this achievement and keep encouraging them!"
            : "Your child's development might need attention. Explore recommendations.",
          status: answeredQuestionIds.has(question.id)
            ? "completed"
            : "pending",
          type: answeredQuestionIds.has(question.id) ? "success" : "warning",
        }));

        setAlerts(processedAlerts);
      } catch (error) {
        console.error("Error fetching alerts:", error);
        setAlerts([]);
      } finally {
        setLoading(false);
      }
    };

    if (childId) {
      fetchAlerts();
    } else {
      setLoading(false);
    }
  }, [childId]);

  const getAlertIcon = (type) => {
    if (type === "success") {
      return (
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
          <span className="text-white text-2xl md:text-3xl font-bold">!</span>
        </div>
      );
    }
    return (
      <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
        <span className="text-white text-2xl md:text-3xl font-bold">!</span>
      </div>
    );
  };

  const getAlertCardColor = (type) => {
    if (type === "success") return "bg-green-50";
    return "bg-blue-50";
  };

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <ClientBackground />
      <div className="relative z-10 min-h-screen pb-8">
        {/* Header */}
        <div className="bg-white/30 backdrop-blur-sm px-5 py-4 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg md:text-xl font-semibold text-[#1e3a5f]">
            Alerts
          </h1>
          <div className="w-10" />
        </div>

        {/* Content */}
        <div className="px-5 md:px-8 lg:px-12 pt-6 pb-8">
          <div className="max-w-4xl mx-auto">
            {alerts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`${getAlertCardColor(
                      alert.type
                    )} rounded-2xl p-5 flex gap-4 shadow-sm`}
                  >
                    {/* Icon */}
                    {getAlertIcon(alert.type)}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm md:text-base font-bold text-[#1e3a5f] mb-2 leading-tight">
                        {alert.title}
                      </h3>
                      <p className="text-xs md:text-sm text-gray-700 leading-relaxed">
                        {alert.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-base">No alerts available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}